import { useState } from 'react';
import { api } from '../utils/api';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';

interface VoiceProps {
  userId: string;
  topic: string;
  sessionId: string | null;
  onSessionIdChange: (sessionId: string) => void;
}

export const Voice = ({ userId, topic, sessionId, onSessionIdChange }: VoiceProps) => {
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { isRecording, startRecording, stopRecording, getAudioBlob, clearRecording } =
    useVoiceRecorder();

  const handleRecordingComplete = async () => {
    const audioBlob = getAudioBlob();
    if (!audioBlob) return;

    setIsProcessing(true);
    setTranscript('');
    setResponse('');

    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);

      reader.onloadend = async () => {
        const base64Audio = (reader.result as string).split(',')[1];

        const voiceResponse = await api.sendVoice(
          userId,
          base64Audio,
          'webm',
          sessionId || undefined,
          topic
        );

        if (!sessionId) {
          onSessionIdChange(voiceResponse.sessionId);
        }

        setTranscript(voiceResponse.transcription);
        setResponse(voiceResponse.responseText);

        // Play audio response
        const audioData = atob(voiceResponse.responseAudio);
        const arrayBuffer = new ArrayBuffer(audioData.length);
        const view = new Uint8Array(arrayBuffer);
        for (let i = 0; i < audioData.length; i++) {
          view[i] = audioData.charCodeAt(i);
        }

        const audioBlob = new Blob([arrayBuffer], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.play();

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
        };
      };
    } catch (error) {
      console.error('Error processing voice:', error);
      setResponse('Sorry, I encountered an error processing your voice. Please try again.');
    } finally {
      setIsProcessing(false);
      clearRecording();
    }
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
      handleRecordingComplete();
    } else {
      startRecording();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>Voice Mode</h2>
        <p>Speak with your AI Study Buddy about {topic}</p>
      </div>

      <div style={styles.recordingSection}>
        <button
          onClick={handleToggleRecording}
          disabled={isProcessing}
          style={{
            ...styles.recordButton,
            ...(isRecording ? styles.recordButtonActive : {}),
          }}
        >
          {isRecording ? '🎤 Stop Recording' : '🎤 Start Recording'}
        </button>

        {isRecording && (
          <div style={styles.recordingIndicator}>
            <span style={styles.recordingDot}></span>
            Recording...
          </div>
        )}

        {isProcessing && (
          <div style={styles.processingIndicator}>Processing your voice...</div>
        )}
      </div>

      {transcript && (
        <div style={styles.transcriptSection}>
          <div style={styles.sectionLabel}>You said:</div>
          <div style={styles.transcriptText}>{transcript}</div>
        </div>
      )}

      {response && (
        <div style={styles.responseSection}>
          <div style={styles.sectionLabel}>AI Buddy responded:</div>
          <div style={styles.responseText}>{response}</div>
        </div>
      )}

      {!transcript && !response && !isRecording && !isProcessing && (
        <div style={styles.instructionsSection}>
          <h3>How to use Voice Mode:</h3>
          <ol style={styles.instructionsList}>
            <li>Click the microphone button to start recording</li>
            <li>Speak your question clearly</li>
            <li>Click the button again to stop recording</li>
            <li>AI Buddy will transcribe, respond, and speak back to you!</li>
          </ol>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '30px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  recordingSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '30px',
  },
  recordButton: {
    fontSize: '18px',
    padding: '20px 40px',
    borderRadius: '50px',
    border: '2px solid #007bff',
    backgroundColor: 'white',
    color: '#007bff',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontWeight: 'bold',
  },
  recordButtonActive: {
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
  },
  recordingIndicator: {
    marginTop: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    color: '#dc3545',
  },
  recordingDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#dc3545',
    animation: 'pulse 1.5s infinite',
  },
  processingIndicator: {
    marginTop: '20px',
    fontSize: '16px',
    color: '#666',
  },
  transcriptSection: {
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#e3f2fd',
    borderRadius: '12px',
  },
  responseSection: {
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#f1f8e9',
    borderRadius: '12px',
  },
  sectionLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#666',
  },
  transcriptText: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#333',
  },
  responseText: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#333',
  },
  instructionsSection: {
    textAlign: 'center',
    color: '#666',
    marginTop: '40px',
  },
  instructionsList: {
    textAlign: 'left',
    maxWidth: '500px',
    margin: '20px auto',
    lineHeight: '1.8',
  },
};
